/**
 * Node Form API Client
 * Resolves Atlas Forms registered for a process node.
 *
 * GET /api/v1/process-engine/node-forms/standard/GetNodeForms/{nodeSubUsage}/{processElementId}
 *   ?includeSchema=true&includeChecksum=false
 */

import { BaseApiClient, ApiResponse } from './baseApiClient';
import { API_ENDPOINTS } from '../config/apiEndpoints';
import {
  NODE_SUB_USAGE,
  FORM_DEFAULTS,
  type NodeFormDto,
  type NodeFormsData,
  type NodeSubUsageType,
} from '../types';

/** Recursively converts object keys from PascalCase back to camelCase. */
const toCamelCaseKeys = (obj: any): any => {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) return obj.map(toCamelCaseKeys);
  if (typeof obj === 'object' && obj.constructor === Object) {
    const result: any = {};
    for (const key of Object.keys(obj)) {
      const camelKey = key.charAt(0).toLowerCase() + key.slice(1);
      result[camelKey] = toCamelCaseKeys(obj[key]);
    }
    return result;
  }
  return obj;
};

export class NodeFormApiClient extends BaseApiClient {
  private requestCache = new Map<string, Promise<ApiResponse<NodeFormDto[]>>>();

  /**
   * Generate cache key for request deduplication
   */
  private getCacheKey(processElementId: number, nodeSubUsage: string, includeSchema: boolean, includeChecksum: boolean): string {
    return `${processElementId}:${nodeSubUsage}:${includeSchema}:${includeChecksum}`;
  }

  /**
   * Returns all forms for the given node and sub-usage.
   * Prevents duplicate concurrent requests to the same endpoint (React StrictMode protection).
   *
   * @param processElementId - ProcessElement ID (node instance in the workflow)
   * @param nodeSubUsage     - Sub-usage path segment (default: 'DesignTime')
   * @param includeSchema    - When true, each result includes the full Atlas Form schema
   * @param includeChecksum  - When true, schema checksums are computed and returned
   */
  async getNodeForms(
    processElementId: number,
    nodeSubUsage: NodeSubUsageType | string = FORM_DEFAULTS.nodeSubUsage,
    includeSchema: boolean = FORM_DEFAULTS.includeSchema,
    includeChecksum: boolean = FORM_DEFAULTS.includeChecksum
  ): Promise<ApiResponse<NodeFormDto[]>> {
    const cacheKey = this.getCacheKey(processElementId, String(nodeSubUsage), includeSchema, includeChecksum);

    // Return cached promise if request is already in flight
    if (this.requestCache.has(cacheKey)) {
      return this.requestCache.get(cacheKey)!;
    }

    // Create and cache the promise
    const requestPromise = (async () => {
      try {
        const params = new URLSearchParams();
        params.append('includeSchema', String(includeSchema));
        params.append('includeChecksum', String(includeChecksum));

        const endpoint = `${API_ENDPOINTS.NODE_FORMS.GET(nodeSubUsage, processElementId)}?${params.toString()}`;
        const raw = await this.get<NodeFormsData>(endpoint);

        if (!raw.success || !raw.data) {
          return raw as unknown as ApiResponse<NodeFormDto[]>;
        }
        return {
          success: true,
          // Apply toCamelCaseKeys to the full form so every property (formID, formName,
          // nodeUsage, schema…) is camelCase. Grid column keys and all callers use camelCase.
          data: (raw.data.NodeForms ?? []).map(form => toCamelCaseKeys(form)) as NodeFormDto[],
        };
      } finally {
        // Clean up cache after request completes
        this.requestCache.delete(cacheKey);
      }
    })();

    this.requestCache.set(cacheKey, requestPromise);
    return requestPromise;
  }

  /**
   * Shorthand: Returns design-time configuration forms with schema always included.
   *
   * @param processElementId - ProcessElement ID
   * @param nodeSubUsage     - Sub-usage filter (default: 'DesignTime')
   */
  async getNodeFormsBySubUsage(
    processElementId: number,
    nodeSubUsage: NodeSubUsageType | string = NODE_SUB_USAGE.NodeDesignConfiguration
  ): Promise<ApiResponse<NodeFormDto[]>> {
    return this.getNodeForms(processElementId, nodeSubUsage, true, false);
  }
}

export const nodeFormApiClient = new NodeFormApiClient();
export default nodeFormApiClient;
